import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit, ExternalLink, Search, Trash2, User, X } from 'lucide-react';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '~/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import { apiClient, type Idol } from '~/lib/api';

export default function IdolsPage() {
  const queryClient = useQueryClient();
  const [editingIdol, setEditingIdol] = useState<Idol | null>(null);
  const [deletingIdol, setDeletingIdol] = useState<Idol | null>(null);
  const [formData, setFormData] = useState({ slug: '', name: '', jpName: '', avatar: '' });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const limit = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-idols', page, search],
    queryFn: () => apiClient.getIdols({ page, limit, search }),
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; slug: string; name: string; jpName?: string; avatar?: string }) =>
      apiClient.updateIdol(data.id, { slug: data.slug, name: data.name, jpName: data.jpName, avatar: data.avatar }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-idols'] });
      setEditingIdol(null);
      setFormData({ slug: '', name: '', jpName: '', avatar: '' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteIdol(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-idols'] });
      setDeletingIdol(null);
    },
  });

  const handleEdit = (idol: Idol) => {
    setEditingIdol(idol);
    setFormData({
      slug: idol.slug,
      name: idol.name,
      jpName: idol.jpName || '',
      avatar: idol.avatar || '',
    });
  };

  const handleSave = () => {
    if (editingIdol && formData.slug && formData.name) {
      updateMutation.mutate({
        id: editingIdol.id,
        slug: formData.slug,
        name: formData.name,
        jpName: formData.jpName || undefined,
        avatar: formData.avatar || undefined,
      });
    }
  };

  const handleDelete = () => {
    if (deletingIdol) {
      deleteMutation.mutate(deletingIdol.id);
    }
  };

  const handleSearchSubmit = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const getIdolUrl = (slug: string) => {
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://javfacts.com';
    return `${baseUrl}/en/idols/${slug}`;
  };

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-3xl font-bold tracking-tight'>Idols Management</h1>
        <p className='text-muted-foreground'>Browse and manage all idols/casts in the database</p>
      </div>

      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle>All Idols</CardTitle>
              <CardDescription>{data?.data ? `${data.data.total} idols total` : 'Loading...'}</CardDescription>
            </div>
            <div className='flex items-center gap-2'>
              <div className='relative'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                <Input
                  placeholder='Search by slug or name...'
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchSubmit();
                    }
                  }}
                  onBlur={handleSearchSubmit}
                  className='pl-9 w-[300px]'
                />
                {searchInput && (
                  <Button
                    variant='ghost'
                    size='sm'
                    className='absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0'
                    onClick={handleSearchClear}
                  >
                    <X className='h-4 w-4' />
                  </Button>
                )}
              </div>
              {search && (
                <Button variant='secondary' size='sm' onClick={handleSearchClear}>
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='text-center py-8'>
              <p className='text-muted-foreground'>Loading idols...</p>
            </div>
          ) : error ? (
            <div className='text-center py-8'>
              <p className='text-destructive'>Error loading idols</p>
            </div>
          ) : data?.data?.items.length === 0 ? (
            <div className='text-center py-8'>
              <p className='text-muted-foreground'>{search ? 'No idols match your search' : 'No idols found'}</p>
              {search && (
                <Button
                  variant='link'
                  onClick={() => {
                    setSearch('');
                    setPage(1);
                  }}
                  className='mt-2'
                >
                  Clear search
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className='rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='w-[70px]'>Avatar</TableHead>
                      <TableHead className='w-[150px]'>Slug</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Japanese Name</TableHead>
                      <TableHead className='w-[120px]'>Videos</TableHead>
                      <TableHead className='w-[200px]'>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.data?.items.map((idol: any) => (
                      <TableRow key={idol.id}>
                        <TableCell>
                          {idol.avatar ? (
                            <img src={idol.avatar} alt={idol.name} className='w-10 h-10 rounded-full object-cover' />
                          ) : (
                            <div className='w-10 h-10 rounded-full bg-muted flex items-center justify-center'>
                              <User className='h-5 w-5 text-muted-foreground' />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className='font-mono text-xs'>{idol.slug}</TableCell>
                        <TableCell>
                          <span className='font-medium'>{idol.name}</span>
                        </TableCell>
                        <TableCell>
                          {idol.jpName ? (
                            <span className='text-sm'>{idol.jpName}</span>
                          ) : (
                            <span className='text-muted-foreground text-sm'>N/A</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant='secondary'>{idol._count.videos} videos</Badge>
                        </TableCell>
                        <TableCell>
                          <div className='flex items-center gap-2'>
                            <Button variant='ghost' size='sm' onClick={() => handleEdit(idol)}>
                              <Edit className='h-4 w-4' />
                            </Button>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => setDeletingIdol(idol)}
                              className='text-destructive hover:text-destructive'
                            >
                              <Trash2 className='h-4 w-4' />
                            </Button>
                            <a
                              href={getIdolUrl(idol.slug)}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
                            >
                              <ExternalLink className='h-4 w-4' />
                            </a>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {data?.data && data.data.totalPages > 1 && (
                <div className='flex items-center justify-between mt-4'>
                  <p className='text-sm text-muted-foreground'>
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, data.data.total)} of {data.data.total}{' '}
                    idols
                  </p>
                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setPage((p) => Math.min(data.data.totalPages, p + 1))}
                      disabled={page === data.data.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Sheet */}
      <Sheet open={!!editingIdol} onOpenChange={(open) => !open && setEditingIdol(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Edit Idol</SheetTitle>
            <SheetDescription>Update idol information</SheetDescription>
          </SheetHeader>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor='slug'>
                Slug <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='slug'
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder='idol-slug'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='name'>
                English Name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='name'
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder='Idol Name'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='jpName'>Japanese Name</Label>
              <Input
                id='jpName'
                value={formData.jpName}
                onChange={(e) => setFormData({ ...formData, jpName: e.target.value })}
                placeholder='アイドル名'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='avatar'>Avatar URL</Label>
              <Input
                id='avatar'
                value={formData.avatar}
                onChange={(e) => setFormData({ ...formData, avatar: e.target.value })}
                placeholder='https://example.com/avatar.jpg'
              />
              {formData.avatar && (
                <div className='mt-2'>
                  <img
                    src={formData.avatar}
                    alt='Preview'
                    className='w-16 h-16 rounded-full object-cover'
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <SheetFooter>
            <Button variant='outline' onClick={() => setEditingIdol(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!formData.slug || !formData.name || updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingIdol} onOpenChange={(open) => !open && setDeletingIdol(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Idol</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "<strong>{deletingIdol?.name}</strong>"?
              {deletingIdol && deletingIdol._count.videos > 0 && (
                <span className='block mt-2 text-destructive font-semibold'>
                  This idol has {deletingIdol._count.videos} associated videos and cannot be deleted.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deletingIdol?._count.videos! > 0 || deleteMutation.isPending}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
